import { AreaChart, Area, XAxis, YAxis, CartesianGrid } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Building2, TrendingUp } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface TenantsByDay {
  date: string;
  count: number;
  cumulative: number;
}

interface TenantGrowthChartProps {
  data: TenantsByDay[];
  totalTenants: number;
  isLoading?: boolean;
}

const chartConfig = {
  cumulative: {
    label: 'Total Clientes',
    color: 'hsl(var(--primary))',
  },
  count: {
    label: 'Novos',
    color: 'hsl(var(--chart-2))',
  },
};

export function TenantGrowthChart({ data, totalTenants, isLoading }: TenantGrowthChartProps) {
  const formattedData = data.map(item => ({
    ...item,
    dateLabel: format(parseISO(item.date), 'dd/MM', { locale: ptBR }),
  }));

  const newTenants = data.reduce((sum, d) => sum + d.count, 0);
  const growthRate = data.length > 1 && data[0].cumulative > 0
    ? Math.round(((data[data.length - 1].cumulative - data[0].cumulative) / data[0].cumulative) * 100)
    : 0;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Crescimento de Clientes
            </CardTitle>
            <CardDescription>Evolução do número de organizações</CardDescription>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">{totalTenants}</p>
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <TrendingUp className="h-3 w-3 text-green-500" />
              <span className="text-green-500 font-medium">+{newTenants}</span>
              <span>no período</span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[250px] w-full" />
        ) : data.length === 0 || data.every(d => d.cumulative === 0) ? (
          <div className="h-[250px] flex items-center justify-center text-muted-foreground text-sm">
            Nenhum dado no período selecionado
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="h-[250px] w-full">
            <AreaChart data={formattedData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="tenantGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis 
                dataKey="dateLabel" 
                tickLine={false} 
                axisLine={false}
                tickMargin={8}
                interval="preserveStartEnd"
              />
              <YAxis 
                tickLine={false} 
                axisLine={false}
                tickMargin={8}
                allowDecimals={false}
              />
              <ChartTooltip 
                content={<ChartTooltipContent />}
                labelFormatter={(value) => value}
              />
              <Area
                type="monotone"
                dataKey="cumulative"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fill="url(#tenantGradient)"
              />
            </AreaChart>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
}
